/**
 * Lobby scene — three-column layout matching the WhatIs polish bar:
 *
 *   [ left: TOP 10 + HOW TO PLAY ] [ center: ANAGRAM title + prompt ] [ right: STANDINGS ]
 *
 * Mounted as an absolute-positioned grid inside the 1920x1080 stage. The
 * left column stacks the top-10 leaderboard above the how-to panel via
 * justify-content: space-evenly so vertical gaps stay even at any height.
 *
 * Background is a subtle radial sweep + soft glow + star field (animated-bg);
 * the title shimmers in the same yellow-on-blue brand as the round scene.
 */

import type { LbRow } from '@sg/shared';
import {
  createLobbyLeaderboardTop,
  createLobbyLeaderboardTail,
  type LobbyLeaderboardHandle,
} from '../ui/lobby-leaderboard.js';
import { createLobbyHowTo, type LobbyHowToHandle } from '../ui/lobby-howto.js';

export interface LobbyHandle {
  el: HTMLDivElement;
  setLeaderboard(rows: LbRow[], allTime: LbRow[]): void;
  setVisible(v: boolean): void;
}

const STYLES = `
  @keyframes an-lobby-glow {
    0%, 100% { text-shadow: 0 4px 24px rgba(6,12,233,0.55), 0 0 30px rgba(255,196,61,0.3),  0 0 60px rgba(0,191,166,0.0);  color: var(--sg-primary, #FFC43D); }
    50%      { text-shadow: 0 4px 24px rgba(6,12,233,0.55), 0 0 90px rgba(255,196,61,0.95), 0 0 30px rgba(0,191,166,0.45); color: #FFD466; }
  }
  @keyframes an-lobby-bounce {
    0%, 100% { transform: translateY(0); }
    50%      { transform: translateY(-4px); }
  }
  @keyframes an-lobby-letter-jiggle {
    0%, 100% { transform: translateY(0) rotate(0); }
    50%      { transform: translateY(-6px) rotate(-1.5deg); }
  }
  .an-lobby-wrap {
    position: absolute;
    inset: 0;
    display: grid;
    grid-template-columns: 27% 43% 27%;
    gap: 1.5%;
    padding: 27px 38px;
    box-sizing: border-box;
    pointer-events: none;
    color: #fff;
    z-index: 40;
  }
  .an-lobby-col-center {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1.4rem;
    text-align: center;
  }
  .an-lobby-col-left,
  .an-lobby-col-right {
    display: flex;
    flex-direction: column;
    min-height: 0;
  }
  .an-lobby-col-left {
    justify-content: space-evenly;
    align-items: stretch;
  }
  .an-lobby-col-left > :nth-child(1),
  .an-lobby-col-left > :nth-child(2) {
    flex: 0 0 auto;
  }
  .an-lobby-title {
    font-family: 'Oswald','Bebas Neue',Impact,sans-serif;
    font-size: 10.2rem;
    letter-spacing: 0.02em;
    font-weight: 700;
    color: var(--sg-primary, #FFC43D);
    line-height: 0.9;
    white-space: nowrap;
    animation: an-lobby-glow 4s ease-in-out infinite;
  }
  .an-lobby-title span {
    display: inline-block;
    animation: an-lobby-letter-jiggle 5s ease-in-out infinite;
  }
  .an-lobby-title span:nth-child(1) { animation-delay: 0s; }
  .an-lobby-title span:nth-child(2) { animation-delay: 0.18s; }
  .an-lobby-title span:nth-child(3) { animation-delay: 0.36s; }
  .an-lobby-title span:nth-child(4) { animation-delay: 0.54s; }
  .an-lobby-title span:nth-child(5) { animation-delay: 0.72s; }
  .an-lobby-title span:nth-child(6) { animation-delay: 0.90s; }
  .an-lobby-title span:nth-child(7) { animation-delay: 1.08s; }
  .an-lobby-sub {
    font-size: 1.1rem;
    letter-spacing: 0.35em;
    color: #94A3B8;
    text-transform: uppercase;
    margin-top: -0.25rem;
  }
  .an-lobby-prompt {
    margin-top: 1.5rem;
    font-family: 'Oswald','Bebas Neue',Impact,sans-serif;
    font-size: 1.7rem;
    letter-spacing: 0.06em;
    color: #E2E8F0;
    background: linear-gradient(180deg, rgba(6,12,233,0.55) 0%, rgba(3,4,40,0.7) 100%);
    padding: 1rem 1.6rem;
    border-radius: 14px;
    border: 2px solid var(--sg-primary, #FFC43D);
    box-shadow: 0 0 40px rgba(255,196,61,0.25), 0 6px 30px rgba(0,0,0,0.5);
    animation: an-lobby-bounce 2.4s ease-in-out infinite;
  }
  .an-lobby-prompt code {
    color: var(--sg-primary, #FFC43D);
    background: rgba(255,196,61,0.12);
    padding: 0.1em 0.4em;
    border-radius: 6px;
    font-family: ui-monospace, monospace;
    font-size: 1em;
    margin: 0 0.15em;
  }
  .an-lobby-foot {
    margin-top: 0.75rem;
    font-size: 0.95rem;
    opacity: 0.75;
    letter-spacing: 0.05em;
    max-width: 90%;
  }
  .an-lobby-foot code {
    color: var(--sg-accent, #00BFA6);
    background: rgba(0,191,166,0.10);
    padding: 0 0.35em;
    border-radius: 4px;
    font-family: ui-monospace, monospace;
  }
`;

function ensureStyles(): void {
  if (document.getElementById('an-lobby-style')) return;
  const s = document.createElement('style');
  s.id = 'an-lobby-style';
  s.textContent = STYLES;
  document.head.appendChild(s);
}

export function createLobby(stage: HTMLElement): LobbyHandle {
  ensureStyles();
  const el = document.createElement('div');
  el.className = 'an-lobby-wrap';
  el.setAttribute('data-scene', 'lobby');
  el.setAttribute('data-testid', 'an-lobby');
  el.innerHTML = `
    <div class="an-lobby-col-left" data-role="col-left"></div>
    <div class="an-lobby-col-center">
      <div class="an-lobby-title" data-testid="an-lobby-title">
        <span>A</span><span>N</span><span>A</span><span>G</span><span>R</span><span>A</span><span>M</span>
      </div>
      <div class="an-lobby-sub">a game on The Gamer Network</div>
      <div data-testid="lobby-prompt" class="an-lobby-prompt">
        Streamer: type <code>!start</code> in chat to begin
      </div>
      <div class="an-lobby-foot">
        Viewers: when the round opens, type the unscrambled word in chat. Type <code>!score</code> for your stats.
      </div>
    </div>
    <div class="an-lobby-col-right" data-role="col-right"></div>
  `;
  stage.appendChild(el);

  const leftCol = el.querySelector<HTMLDivElement>('[data-role="col-left"]')!;
  const rightCol = el.querySelector<HTMLDivElement>('[data-role="col-right"]')!;

  const lbTop: LobbyLeaderboardHandle = createLobbyLeaderboardTop(leftCol);
  const howTo: LobbyHowToHandle = createLobbyHowTo(leftCol);
  const lbTail: LobbyLeaderboardHandle = createLobbyLeaderboardTail(rightCol);
  lbTop.setRows([]);
  lbTail.setRows([]);
  void howTo;

  return {
    el,
    setLeaderboard(rows, allTime) {
      lbTop.setRows(allTime);
      lbTail.setRows(allTime);
      void rows; // current-game leaderboard is only useful in side panel
    },
    setVisible(v) {
      el.style.display = v ? 'grid' : 'none';
    },
  };
}
